import { useResource } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";

/**
 * The compliance dashboard (docs handoff, adapted to repo conventions): one
 * page, seven panels, for all users - each seeing only their own data. The
 * scoping comes entirely from RLS and the role gates that already exist; this
 * page adds NO new access and NO new endpoints. A panel whose data has no
 * endpoint yet says "not yet measured" out loud - absence is shown, never
 * implied away.
 *
 * Colour is never the only signal: every status carries a chip class AND a
 * word, and the two expiry systems are told apart in words on the panel
 * itself ("Blocks rostering" vs "Reminder only").
 */

interface DashboardCounts {
    counts?: {
        active_staff?: number; active_participants?: number;
        open_incidents?: number; new_referrals?: number;
    };
    hrCompliancePercent?: number;
    compliantStaff?: number;
    totalActiveStaff?: number;
}

interface ClockRow {
    id: string;
    notify_due_at: string | null;
    detail_due_at: string;
    notified_at: string | null;
    detail_submitted_at: string | null;
    final_report_due_at: string | null;
    final_report_submitted_at: string | null;
}
interface Wall { clocks: ClockRow[]; unstarted: unknown[] }

interface ReferralRow { received_at: string; status: string }

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="card">
        <div className="card-head"><h2>{title}</h2></div>
        <div className="card-body">{children}</div>
    </div>
);

const NotVisible = () => (
    <div className="empty">Not visible for this role — the API scopes what each account may read.</div>
);

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>{value}</div>
        <div style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{label}</div>
    </div>
);

export const ComplianceDashboard = () => {
    const { user } = useAuth();
    const today = useResource<DashboardCounts>("/dashboard");
    const docs = useResource<unknown>("/compliance/documents");
    const wall = useResource<Wall>("/commission-clocks");
    const referrals = useResource<ReferralRow[]>("/referrals");

    const docCount = Array.isArray(docs.data) ? docs.data.length
        : docs.data && typeof docs.data === "object" && Array.isArray((docs.data as { documents?: unknown[] }).documents)
            ? (docs.data as { documents: unknown[] }).documents.length
            : null;

    const clockStats = (() => {
        const clocks = wall.data?.clocks ?? [];
        const now = Date.now();
        let overdue = 0, open = 0;
        for (const c of clocks) {
            const steps: Array<[string | null, string | null]> = [
                [c.notify_due_at, c.notified_at],
                [c.detail_due_at, c.detail_submitted_at],
                [c.final_report_due_at, c.final_report_submitted_at],
            ];
            let clockOpen = false, clockOverdue = false;
            for (const [due, done] of steps) {
                if (!due || done) continue;
                clockOpen = true;
                if (new Date(due).getTime() < now) clockOverdue = true;
            }
            if (clockOpen) open += 1;
            if (clockOverdue) overdue += 1;
        }
        return { total: clocks.length, open, overdue, unstarted: wall.data?.unstarted.length ?? 0 };
    })();

    const triage = (() => {
        if (!Array.isArray(referrals.data)) return null;
        const fresh = referrals.data.filter((r) => r.status === "new");
        const late = fresh.filter((r) =>
            Date.now() - new Date(r.received_at).getTime() > 24 * 3_600_000);
        return { waiting: fresh.length, late: late.length };
    })();

    return (
        <div style={{ display: "grid", gap: "0.85rem" }}>
            {/* 1. Today */}
            <Panel title="Today">
                {today.data?.counts ? (
                    <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                        <Stat label="Active staff" value={today.data.counts.active_staff ?? "—"} />
                        <Stat label="Active participants" value={today.data.counts.active_participants ?? "—"} />
                        <Stat label="Open incidents" value={today.data.counts.open_incidents ?? "—"} />
                        <Stat label="New referrals" value={today.data.counts.new_referrals ?? "—"} />
                    </div>
                ) : <NotVisible />}
            </Panel>

            {/* 2. The two expiry systems, told apart in words */}
            <Panel title="The two expiry systems">
                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 240px" }}>
                        <span className="chip block">⛔ Blocks rostering</span>
                        <p style={{ fontSize: "0.85rem", marginTop: "0.4rem" }}>
                            Training and competencies (rule R1). An expired mandatory competency
                            stops the roster assigning that worker — it cannot be overridden.
                        </p>
                        {today.data ? (
                            <Stat label={`of ${today.data.totalActiveStaff ?? "—"} active staff hold every required competency`}
                                value={`${today.data.hrCompliancePercent ?? "—"}%`} />
                        ) : <NotVisible />}
                    </div>
                    <div style={{ flex: "1 1 240px" }}>
                        <span className="chip warn">⏰ Reminder only</span>
                        <p style={{ fontSize: "0.85rem", marginTop: "0.4rem" }}>
                            Employee documents (migration 040's own words): expiry observes and
                            reminds at 60/30/7 days — it never blocks a shift.
                        </p>
                        {docCount !== null
                            ? <Stat label="documents tracked for expiry" value={docCount} />
                            : <NotVisible />}
                    </div>
                </div>
            </Panel>

            {/* 3. RN triage vs the 24-hour commitment */}
            <Panel title="Referral triage against the 24-hour commitment">
                {triage ? (
                    <div style={{ display: "flex", gap: "1.5rem", alignItems: "center", flexWrap: "wrap" }}>
                        <Stat label="new referrals awaiting triage" value={triage.waiting} />
                        <div>
                            {triage.late > 0
                                ? <span className="chip block">⚠ {triage.late} waiting longer than 24 hours</span>
                                : <span className="chip pass">✓ none waiting longer than 24 hours</span>}
                        </div>
                    </div>
                ) : <NotVisible />}
            </Panel>

            {/* 4. Shifts blocked, by rule */}
            <Panel title="Shifts blocked, by rule">
                <div className="empty">
                    Not yet measured — the rules engine refuses non-compliant assignments at
                    the moment of rostering, but no aggregate of block reasons is recorded.
                    R1 (expired mandatory training) and R8 (excluded worker) cannot be
                    overridden; the others record an audited override.
                </div>
            </Panel>

            {/* 5. Reportable incident deadlines */}
            <Panel title="Reportable incident deadlines (Commission clocks)">
                {wall.data ? (
                    <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                        <Stat label="clocks running" value={clockStats.open} />
                        <div>
                            {clockStats.overdue > 0
                                ? <span className="chip block">⛔ {clockStats.overdue} overdue</span>
                                : <span className="chip pass">✓ nothing overdue</span>}
                            {clockStats.unstarted > 0 && (
                                <span className="chip warn" style={{ marginLeft: 6 }}>
                                    ⚠ {clockStats.unstarted} flagged reportable, no clock started
                                </span>
                            )}
                        </div>
                    </div>
                ) : <NotVisible />}
            </Panel>

            {/* 6. The three unbuilt states — absence made visible */}
            <Panel title="Not yet tracked (named so absence is visible)">
                <ul style={{ fontSize: "0.85rem", margin: 0, paddingLeft: "1.1rem" }}>
                    <li>Worker screening as a six-state lifecycle (currently a document with an expiry date)</li>
                    <li>The two seven-year retention clocks and disposal decisions</li>
                    <li>Consent expiry and re-consent campaigns</li>
                </ul>
            </Panel>

            {/* 7. Role-scoped view note */}
            <Panel title="What this page shows you">
                <p style={{ fontSize: "0.85rem", margin: 0 }}>
                    Every panel is scoped by the database itself, not by hiding numbers:
                    signed in as <strong>{user?.role ?? "—"}</strong>, you see only rows your
                    role may read — a partner sees only their own organisation's clocks, staff
                    see their own credentials. The API enforces this on every request.
                </p>
            </Panel>
        </div>
    );
};

export default ComplianceDashboard;
